/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BuilderUserContext, isJobEditable } from '@gh/builder-shared';
import { BuilderPermission } from '@gh/builder-shared-data';
import {
  FormStateSnapshot,
  getFormStateSnapshotValue,
  hasFormStateError,
  isRouteForCreationMode,
  markFormStateAsTouched,
  MutablePageState,
  setEntityFields,
  setFormStateSnapshotValue,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { FormMode } from '@gh/core-ui';
import { isNotNil } from '@gh/core-util';
import { cloneDeep, find, first, isNil, last, map, omit, pull, without } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { jobSpecQuoteChangeFromSection, validateJobSpecSectionInfo } from '../../';
import {
  getJobSpecQuoteChangeId,
  isNotQcParagraph,
  JobSpecDetails,
  JobSpecInfo,
  JobSpecParagraphDetails,
  JobSpecParagraphInfo,
  JobSpecQuoteChange,
  jobSpecQuoteChangeArr,
  jobSpecQuoteChangeToSection,
  JobSpecSectionDetails,
  JobSpecSectionInfo,
  JobSpecSelection,
  JobSpecSelectionLevel,
  jobSpecTreeHelper,
  validateJobSpecParagraphInfo,
} from '../../resources/job.model';

interface JobSpecHistoryEntry {
  state: JobSpecDetails;
  selection: {
    paragraphId?: ID;
    sectionId?: ID;
  };
}

@Injectable()
export class JobSpecDetailsFormState extends MutablePageState<JobSpecDetails> {
  private selection$$ = new BehaviorSubject<JobSpecSelection>({});
  history: JobSpecHistoryEntry[] = [];
  historyPosition: number = 0;
  historyLength = 13;
  private hasOnlyContactMenuChanges: boolean = false;
  private historyCoalesceKey: string | undefined;

  constructor(private route: ActivatedRoute,
              private userContext: BuilderUserContext) {
    super();
  }

  get selection(): JobSpecSelection {
    return this.selection$$.value;
  }

  get selectionChanges(): Observable<JobSpecSelection> {
    return this.selection$$.asObservable();
  }

  get quoteChanges(): JobSpecQuoteChange[] {
    const quoteChangesParagraph = last(this.value.paragraphs);

    return quoteChangesParagraph && quoteChangesParagraph.quoteChanges || [];
  }

  get invalid(): boolean {
    // check validity of top form
    const { masterInfo } = this.value;
    if (hasFormStateError(masterInfo)) {
      return true;
    }

    // check validity of tree content
    return jobSpecTreeHelper.reduceNodes(
      this.value.paragraphs,
      (invalid: boolean, node, _1, _2, path) =>
        invalid || this.isNodeInvalid(path),
      false);
  }

  get isContractSigned(): boolean {
    return this.value.isContractSigned;
  }

  build(job: JobSpecDetails): void {
    this.setMode(isRouteForCreationMode(this.route)
      ? FormMode.CreateMode
      : (this.userContext.hasPermission(BuilderPermission.JobAdmin.JobSpecifications.CREATE_EDIT)
      && isJobEditable(getFormStateSnapshotValue(job.masterInfo))
        ? FormMode.EditMode : FormMode.ViewMode));
    this.setValue(job);
    this.resetDirty();
    this.hasOnlyContactMenuChanges = false;
    this.selectFirstParagraph();
    this.initHistory();
  }

  merge(details: JobSpecDetails): void {
    jobSpecTreeHelper.merge(this.value.paragraphs, details.paragraphs, (thisNode, anotherNode, level) => {
      if (level === JobSpecSelectionLevel.Section) {
        thisNode.isDraggable = false;
        thisNode.info = setFormStateSnapshotValue<JobSpecSectionInfo>(
          thisNode.info,
          (info) =>
            setEntityFields(info, {
              clause: void 0,
              'clauses': map(info.clauses, (clause) => omit(clause, 'quoteChange')),
            }));
      }
    });

    this.value.version = details.version;
    this.hasOnlyContactMenuChanges = false;
    this.emitChange();
    // Baseline on server/synced document: undo must not reference pre-merge snapshots.
    this.initHistory();
  }

  highlightAll(): void {
    // highlight top form
    const { masterInfo } = this.value;
    if (hasFormStateError(masterInfo)) {
      this.value.masterInfo = markFormStateAsTouched(masterInfo);
      return;
    }

    // find the first invalid tree node
    const invalidPath = jobSpecTreeHelper.reduceNodes(
      this.value.paragraphs,
      (invalid, node, _1, _2, path) => {
        if (invalid) {
          return invalid;
        }

        return this.isNodeInvalid(path) ? path : void 0;
      },
      <Maybe<any[]>>void 0);

    if (invalidPath) {
      const lastNode = last(invalidPath)!;
      lastNode.info = markFormStateAsTouched(lastNode.info);
      this.selectByPath(invalidPath);
    }
  }

  selectByPath(path: any[]): void {
    switch (path.length) {
      case JobSpecSelectionLevel.Paragraph:
        this.selectParagraph(path[0]);
        break;
      case JobSpecSelectionLevel.Section:
        this.selectSection(path[0], path[1]);
        break;
      default:
        this.resetSelection();
        break;
    }
  }

  selectParagraph(paragraph: JobSpecParagraphDetails, options?: { recordNavigationHistory?: boolean }): void {
    const previousSelection = this.selection;
    this.selection$$.next({
      level: JobSpecSelectionLevel.Paragraph,
      paragraph,
      section: void 0,
    });
    if (options?.recordNavigationHistory) {
      this.recordTreeNavigationInHistory(previousSelection);
    }
  }

  selectSection(paragraph: JobSpecParagraphDetails, section: JobSpecSectionDetails,
                options?: { recordNavigationHistory?: boolean }): void {
    const previousSelection = this.selection;
    this.selection$$.next({
      level: JobSpecSelectionLevel.Section,
      paragraph,
      section,
    });
    if (options?.recordNavigationHistory) {
      this.recordTreeNavigationInHistory(previousSelection);
    }
  }

  resetSelection(): void {
    this.selection$$.next({
      level: void 0,
      paragraph: void 0,
      section: void 0,
    });
  }

  initHistory(): void {    
    this.history = [];
    this.historyPosition = 0;
    this.resetHistoryCoalesce();
    this.addToHistory(this.value);
  }

  addToHistory(value: JobSpecDetails): void {
    const currentSelection = this.selection;
    const history = this.history.slice(0, this.historyPosition + 1);
    const newHistoryEntry: JobSpecHistoryEntry = {
      state: cloneDeep(value),
      selection: {
        paragraphId: currentSelection.paragraph?.id,
        sectionId: currentSelection.section?.id,
      },
    };
    
    if (history.length < this.historyLength) {
      history.push(newHistoryEntry);
    } else {
      history.shift();
      history.push(newHistoryEntry);
    }
    
    this.historyPosition = history.length - 1;
    this.history = history;
  }

  private resetHistoryCoalesce(): void {
    this.historyCoalesceKey = undefined;
  }
  
  private pushOrCoalesceHistory(value: JobSpecDetails, coalesceKey: string): void {
    const currentSelection = this.selection;
    if (this.historyCoalesceKey === coalesceKey && this.history.length > 0) {
      const historyEntry = this.history[this.historyPosition];
      historyEntry.state = cloneDeep(value);
      historyEntry.selection = {
        paragraphId: currentSelection.paragraph?.id,
        sectionId: currentSelection.section?.id,
      };
      return;
    }
    this.historyCoalesceKey = coalesceKey;
    this.addToHistory(value);
  }

  undo(): void {
    if (this.undoDisabled) {
      return;
    }
    this.resetHistoryCoalesce();
    this.hasOnlyContactMenuChanges = false;
    this.historyPosition = this.historyPosition - 1;
    const historyEntry = this.history[this.historyPosition];
    const undoState = cloneDeep(historyEntry.state);
    this.setValue(undoState);
    this.emitChange();
    setTimeout(() => {
      this.restoreSelection(
        historyEntry.selection.paragraphId,
        historyEntry.selection.sectionId
      );
    }, 0);
  }

  redo(): void {
    if (this.redoDisabled) {
      return;
    }
    this.resetHistoryCoalesce();
    this.hasOnlyContactMenuChanges = false;
    this.historyPosition++;
    const historyEntry = this.history[this.historyPosition];    
    const redoState = cloneDeep(historyEntry.state);
    this.setValue(redoState);
    this.emitChange();
    setTimeout(() => {
      this.restoreSelection(
        historyEntry.selection.paragraphId,
        historyEntry.selection.sectionId
      );
    }, 0);
  }

  private restoreSelection(paragraphId: ID | undefined, sectionId: ID | undefined): void {
    if (!paragraphId) {
      this.selectFirstParagraph();
      return;
    }
    const paragraph = this.value.paragraphs.find(p => p.id === paragraphId);
    if (!paragraph) {
      this.selectFirstParagraph();
      return;
    }
    if (sectionId) {
      const section = paragraph.children.find(s => s.id === sectionId);
      if (section) {
        this.selectSection(paragraph, section);
        return;
      }
    }
    this.selectParagraph(paragraph);
  }

  get undoDisabled(): boolean {
    // Basic checks: no history, only one entry, or at the beginning
    if (!this.history.length || this.history.length === 1 || this.historyPosition === 0) {
      return true;
    }
    return false;
  }

  get redoDisabled(): boolean {    
    return !this.history.length || this.historyPosition === this.history.length - 1;
  }

  moveNodes(): void {
    this.setDirty();
    this.hasOnlyContactMenuChanges = false;
    this.resetHistoryCoalesce();
    this.addToHistory(this.value);
    this.emitChange();
  }

  markContactMenuChange(): void {
    this.hasOnlyContactMenuChanges = true;
  }

  updateMasterInfo(masterInfo: FormStateSnapshot<JobSpecInfo>): void {
    const previousMasterInfo = this.value.masterInfo;
    const previousMasterInfoValue = getFormStateSnapshotValue(previousMasterInfo);
    const newMasterInfoValue = getFormStateSnapshotValue(masterInfo);
    // Top form only binds jobNumber, name, customerName — makeSnapshot omits specTemplateId, contactId, etc.
    const mergedMasterInfoValue = { ...previousMasterInfoValue, ...newMasterInfoValue };
    const mergedMasterInfo: FormStateSnapshot<JobSpecInfo> = {
      value: mergedMasterInfoValue,
      controlState: masterInfo.controlState ?? previousMasterInfo.controlState,
    };

    const isOnlyContactChange = this.isOnlyContactMenuChange(previousMasterInfoValue, mergedMasterInfoValue);

    this.value.masterInfo = mergedMasterInfo;
    this.setDirty();

    if (this.hasOnlyContactMenuChanges || isOnlyContactChange) {
      this.hasOnlyContactMenuChanges = true;
    } else {
      this.hasOnlyContactMenuChanges = false;
      this.pushOrCoalesceHistory(this.value, 'masterInfo');
    }
    
    this.emitChange();
  }

  private isOnlyContactMenuChange(previous: JobSpecInfo, current: JobSpecInfo): boolean {
    const contactFields: (keyof JobSpecInfo)[] = ['customerName', 'contactId'];
    
    const contactFieldChanged = contactFields.some(key => previous[key] !== current[key]);
    
    if (!contactFieldChanged) {
      return false; // No contact fields changed
    }

    const previousKeys = Object.keys(previous) as (keyof JobSpecInfo)[];
    const currentKeys = Object.keys(current) as (keyof JobSpecInfo)[];
    const allKeys = new Set([...previousKeys, ...currentKeys]);
    
    for (const key of allKeys) {
      if (!contactFields.includes(key)) {
        const prevValue = previous[key];
        const currValue = current[key];
        if (prevValue !== currValue && JSON.stringify(prevValue) !== JSON.stringify(currValue)) {
          return false;
        }
      }
    }
    return true;
  }

  updateParagraphData(paragraph: JobSpecParagraphDetails,
                      paragraphData: FormStateSnapshot<JobSpecParagraphInfo>): void {                        
    paragraph.info = paragraphData;
    this.setDirty();
    this.hasOnlyContactMenuChanges = false;
    this.pushOrCoalesceHistory(this.value, `paragraph:${paragraph.id}`);
    this.emitChange();
  }

  updateSectionData(section: JobSpecSectionDetails,
                    sectionData: FormStateSnapshot<JobSpecSectionInfo>): void {
    section.info = sectionData;
    this.setDirty();
    this.hasOnlyContactMenuChanges = false;
    this.pushOrCoalesceHistory(this.value, `section:${section.id}`);
    this.emitChange();
  }

  updateQuoteChanges(quoteChanges: JobSpecQuoteChange[], skipHistory: boolean = false): void {    
    const quoteChangesParagraph = last(this.value.paragraphs);
    if (quoteChangesParagraph) {
      quoteChangesParagraph.quoteChanges = quoteChanges;
    }
    this.setDirty();
    this.hasOnlyContactMenuChanges = false;
    if (!skipHistory) {
      this.resetHistoryCoalesce();
      this.addToHistory(this.value);
    }
    this.emitChange();
  }

  moveQuoteChangeIntoParagraph(paragraph: JobSpecParagraphDetails, index: number, qc: JobSpecQuoteChange): void {    
    // add quote change into paragraph as section
    paragraph.children.splice(index, 0, jobSpecQuoteChangeToSection(qc));
    // remove moved quote change from set of quote changes
    this.updateQuoteChanges(jobSpecQuoteChangeArr.remove(this.quoteChanges, getJobSpecQuoteChangeId(qc)), true);
    // Reset contact menu changes flag when other changes are made
    this.hasOnlyContactMenuChanges = false;
    this.resetHistoryCoalesce();
    this.addToHistory(this.value);
  }

  moveQuoteChangeIntoSection(section: JobSpecSectionDetails, quoteChange: JobSpecQuoteChange, skipHistory: boolean = false): void {    
    const clause = {
      text: quoteChange.text,
      quoteChange,
      isTextWarning: false,
    };
    section.info = setFormStateSnapshotValue(
      section.info,
      (info) =>
        setEntityFields(
          info,
          {
            clauseValue: clause.text,
            clauses: (info.clauses || []).concat([clause]),
            clause,
          }));
    // remove moved quote change from set of quote changes
    this.updateQuoteChanges(jobSpecQuoteChangeArr.remove(this.quoteChanges, getJobSpecQuoteChangeId(quoteChange)), true);
    // Reset contact menu changes flag when other changes are made
    this.hasOnlyContactMenuChanges = false;
    if (!skipHistory) {
      this.resetHistoryCoalesce();
      this.addToHistory(this.value);
    }
  }

  moveQuoteChangeFromParagraphToSection(sourceParagraph: JobSpecParagraphDetails,
                                        sourceSection: JobSpecSectionDetails,
                                        targetSection: JobSpecSectionDetails): void {
    this.backSectionQuoteChange(sourceParagraph, sourceSection, true);
    const quoteChange = this.getQuoteChange(sourceSection.id);
    if (quoteChange) {      
      this.moveQuoteChangeIntoSection(targetSection, quoteChange, true);
    }
    this.hasOnlyContactMenuChanges = false;
    this.resetHistoryCoalesce();
    this.addToHistory(this.value);
  }

  backSectionQuoteChange(paragraph: JobSpecParagraphDetails, section: JobSpecSectionDetails, skipHistory: boolean = false): void {
    this.ensureExistingNodeSelected([paragraph, section]);

    // remove quote change from paragraph
    pull(paragraph.children, section);
    // add section as quote change
    this.updateQuoteChanges(jobSpecQuoteChangeArr.insertSorted(
      this.quoteChanges,
      jobSpecQuoteChangeFromSection(section),
      'name'), true);
    this.hasOnlyContactMenuChanges = false;
    if (!skipHistory) {
      this.resetHistoryCoalesce();
      this.addToHistory(this.value);
    }
  }

  backClauseQuoteChange(section: JobSpecSectionDetails, quoteChangeToRemove: JobSpecQuoteChange): void {    
    section.info = setFormStateSnapshotValue(section.info, (info) => {
      const quoteChangeClause = find(info.clauses, ({ quoteChange }) => quoteChange === quoteChangeToRemove);
      if (isNil(quoteChangeClause)) {
        return info;
      }

      return setEntityFields(info, {
        clauses: without(info.clauses, quoteChangeClause),
        clause: info.clause === quoteChangeClause ? void 0 : quoteChangeClause,
        clauseValue: info.clause === quoteChangeClause ? void 0 : quoteChangeClause,
      });
    });

    // add section as quote change
    this.updateQuoteChanges(jobSpecQuoteChangeArr.insertSorted(
      this.quoteChanges,
      quoteChangeToRemove,
      'name'), true);
    // Reset contact menu changes flag when other changes are made
    this.hasOnlyContactMenuChanges = false;
    this.resetHistoryCoalesce();
    this.addToHistory(this.value);
  }

  private selectionHistoryKey(sel: JobSpecSelection): string {
    const paragraphId = sel.paragraph?.id ?? '';
    const sectionId = sel.section?.id ?? '';
    return `${paragraphId}\u0001${sectionId}`;
  }

  /** Pushes an undo step when the user moves between paragraphs/sections so undo restores the prior tree focus. */
  private recordTreeNavigationInHistory(previousSelection: JobSpecSelection): void {
    if (!this.editable) {
      return;
    }
    if (this.selectionHistoryKey(previousSelection) === this.selectionHistoryKey(this.selection)) {
      return;
    }
    this.resetHistoryCoalesce();
    this.addToHistory(this.value);
    this.emitChange();
  }

  private selectFirstParagraph(): void {
    const firstParagraph = first(this.value.paragraphs);
    if (firstParagraph) {
      this.selectParagraph(firstParagraph);
    } else {
      this.selection$$.next({});
    }
  }

  private isNodeInvalid(path: any[]): boolean {
    const level = path.length;
    const node = last(path);
    const parent = path[path.length - 2];
    switch (level) {
      case JobSpecSelectionLevel.Paragraph: {
        const data: JobSpecParagraphInfo = getFormStateSnapshotValue(node.info);
        return isNotQcParagraph(node.info) ? isNotNil(validateJobSpecParagraphInfo(data)) : false;
      }
      case JobSpecSelectionLevel.Section: {
        const data: JobSpecSectionInfo = getFormStateSnapshotValue(node.info);
        const parentData: JobSpecParagraphInfo = getFormStateSnapshotValue(parent.info);
        return parentData.isParagraphIncluded && isNotNil(validateJobSpecSectionInfo(data));
      }
      default:
        return false;
    }
  }

  private getQuoteChange(id: ID): Maybe<JobSpecQuoteChange> {
    return jobSpecQuoteChangeArr.find(this.quoteChanges, id);
  }

  private ensureExistingNodeSelected(removedPath: any[]): void {
    const { selection } = this;
    const path = jobSpecTreeHelper.findNextNode(
      this.value.paragraphs,
      removedPath,
      jobSpecTreeHelper.createPath(selection.paragraph, selection.section));

    this.selectByPath(path);
  }
}
